// src/components/alert/DashboardAlerts.tsx

import {
  CloseTwoTone,
  ExpandMoreTwoTone,
  ExpandLessTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  AlertProps,
  AlertTitle,
  Box,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import { SensorId } from '@/types/domain'

import { DDMMYYYYHHMMSS, getRelativeDate, userLocales } from '../../api'
import { useAlertsPool, useAppConfig, useSensorPoolIds } from '../../hooks'

export const DashboardAlerts = ({ ...rest }) => {
  const { appConfig, setAppConfig } = useAppConfig()
  const { alertsAcknowledged, alertsMax, salutationMode, timeMode } = appConfig

  const data = useAlertsPool()

  const sIds: SensorId[] = useSensorPoolIds()

  const [showMore, setShowMore] = useState(true)

  const statusToSeverity: Record<string, AlertProps['severity']> = {
    green: 'success',
    yellow: 'warning',
    red: 'error',
  }

  const visibleAlerts = data
    .slice(0)
    .filter(f => !(alertsAcknowledged ?? []).includes(f.id))

  return (
    <Stack {...rest}>
      <Stack>
        <Typography sx={{ textAlign: 'center', mb: 5 }} variant={'h5'}>
          In {salutationMode === 'default' ? 'Ihrer' : 'Deiner'} Abwesenheit gab
          es folgende relevante Ereignisse:
        </Typography>
      </Stack>

      {data
        .filter(f => sIds.includes(Number(f.attributes.sensor_id)))
        .sort(
          (a, b) =>
            new Date(b.attributes.timestamp).getTime() -
            new Date(a.attributes.timestamp).getTime()
        )
        .filter(f => !alertsAcknowledged?.includes(f.id))
        .slice(0, showMore ? alertsMax : undefined)
        .map(alert => {
          const { status, content, timestamp, title } = alert.attributes
          const severity = statusToSeverity[status]
          const acknowledged = alertsAcknowledged?.includes(alert.id)
          const tsFormated: string =
            timeMode === 'relative'
              ? String(getRelativeDate(timestamp))
              : new Date(timestamp).toLocaleDateString(
                  userLocales,
                  DDMMYYYYHHMMSS
                )

          const onClick = () =>
            setAppConfig(prev => ({
              ...prev,
              alertsAcknowledged: [
                ...(prev.alertsAcknowledged || []),
                alert.id,
              ],
            }))

          const action = (
            <IconButton onClick={onClick}>
              <CloseTwoTone />
            </IconButton>
          )

          return (
            <Collapse in={!acknowledged} key={title}>
              <Alert action={action} severity={severity} sx={{ mb: 2 }}>
                <AlertTitle>{title}</AlertTitle>

                <Typography>{content}</Typography>

                <Typography sx={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                  {tsFormated}
                </Typography>
              </Alert>
            </Collapse>
          )
        })}

      {data.slice(0, alertsMax).filter(f => !alertsAcknowledged?.includes(f.id))
        .length === 0 && (
        <Alert severity={'success'}>
          <AlertTitle>Alles im grünen Bereich</AlertTitle>
          Aktuell liegen keine Alerts vor.
        </Alert>
      )}

      {visibleAlerts.length > alertsMax && (
        <Stack textAlign={'center'}>
          <Box>
            <IconButton
              onClick={() => setShowMore(prev => !prev)}
              sx={{ textAlign: 'center' }}
            >
              {showMore ? <ExpandMoreTwoTone /> : <ExpandLessTwoTone />}
            </IconButton>
          </Box>
        </Stack>
      )}
    </Stack>
  )
}
